/**
 * `/trainer-performance` — the monthly 1:1 review (2026-10-20).
 *
 * One calendar month of every trainer's numbers, each beside last month and
 * the team median: pay and bonus, sessions and occupancy, clients and
 * retention, member ratings, trials → sign-ups and repeat purchases, and the
 * two SOPs — answering the AI's post-session prompt, and the consultation
 * form after a trial. The miss lists name the members, so the call can go
 * through them one by one.
 *
 * This page only RENDERS `GET /api/trainer-performance`, which is the same
 * report `get_trainer_performance` returns; every definition lives in
 * `packages/canonical-actions/src/trainer-performance/compute.ts`, and the
 * "How these are counted" section at the foot says them in words.
 *
 * 2026-10-23 (trainer-review-lane) — the "Monthly review" panel under the
 * trainer chooser, for ADMINS (the server answers anyone else 403, and the
 * panel then does not show): how far the trainer's monthly review got, their
 * four tapped answers, and the owner's private notes with Save.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  type PromptOutcomeTier,
  type TrainerPerformanceEntry,
  type TrainerPerformanceHeadline,
  type TrainerPerformanceHeadlineKey,
  type TrainerPerformancePayMeta,
  type TrainerPerformancePurchase,
  type TrainerPerformanceResponse,
  type TrainerReviewSummary,
} from "../lib/api";
import { studioFormat, studioTimeZone } from "../lib/studio-tz";
import {
  changeSinceLastMonth,
  glanceAgainstTeam,
  METRICS,
  money,
  pct,
  reviewAnswers,
  reviewCallLine,
  reviewStatusLine,
  TEAM_COLUMNS,
  type Metric,
} from "../lib/trainer-performance";

// ---------------------------------------------------------------------------
// Months.
// ---------------------------------------------------------------------------

/** `YYYY-MM` moved by whole months. */
function shiftMonth(month: string, delta: number): string {
  const index =
    Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + delta;
  const year = Math.floor(index / 12);
  return `${year}-${String((index % 12) + 1).padStart(2, "0")}`;
}

/** The month it is now at the studio. */
function currentStudioMonth(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: studioTimeZone(),
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((p) => p.type === "year")?.value ?? "1970";
  const month = parts.find((p) => p.type === "month")?.value ?? "01";
  return `${year}-${month}`;
}

function monthLabel(month: string): string {
  const d = new Date(`${month}-15T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? month
    : new Intl.DateTimeFormat("en-GB", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(d);
}

const MONTH_RE = /^[0-9]{4}-(0[1-9]|1[0-2])$/;
const MONTHS_OFFERED = 12;

// ---------------------------------------------------------------------------
// Formatting.
// ---------------------------------------------------------------------------

const DATE_OPTS: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATE_OPTS).format(d);
}

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

function Change({
  metric,
  now,
  before,
}: {
  readonly metric: Metric;
  readonly now: number | null;
  readonly before: number | null;
}): JSX.Element | null {
  const change = changeSinceLastMonth(metric, now, before);
  if (change === null) return null;
  if (change.d === 0) {
    return <span className="tp-change dc-muted">no change</span>;
  }
  const cls =
    change.tone === "good" ? "score-good" : change.tone === "poor" ? "score-poor" : "dc-muted";
  return (
    <span className={`tp-change ${cls}`} title="Change since last month">
      {change.d > 0 ? "▲" : "▼"} {metric.delta(change.d)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Labels.
// ---------------------------------------------------------------------------

const TIER_LABELS: Readonly<Record<PromptOutcomeTier, string>> = {
  captured: "Note on the session",
  captured_unkeyed: "Note, not linked to the session",
  captured_profile: "Saved to the profile",
  replied_other_member: "Replied — filed to another member",
  replied_no_capture: "Replied — nothing captured",
  silent: "No reply",
};

const COMPONENT_LABELS: Readonly<Record<string, string>> = {
  session_revenue_share: "Revenue share",
  hourly: "Hourly",
  pr_session_fixed: "PR sessions",
  unpriced_session_fixed: "Unpriced sessions (flat rate)",
  bonus_new_signup: "Bonus · new sign-up",
  bonus_renewal: "Bonus · renewal",
  adjustment: "Adjustment",
};

function paySourceLabel(pay: TrainerPerformancePayMeta): string {
  if (pay.source === "unavailable") return "no payroll configuration";
  if (pay.source === "provisional") return "provisional — no payroll run yet";
  return `payroll run · ${pay.payroll_run?.status ?? "?"}`;
}

function productLabel(p: TrainerPerformancePurchase): string {
  return p.kind === "membership" ? `${p.slug} (membership)` : p.slug;
}

// ---------------------------------------------------------------------------
// Pieces.
// ---------------------------------------------------------------------------

function Tile({
  label,
  value,
  sub,
  change,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub?: string | undefined;
  readonly change?: JSX.Element | null | undefined;
}): JSX.Element {
  return (
    <div className="stat-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {change ?? null}
      {sub !== undefined ? <div className="tp-sub subtle">{sub}</div> : null}
    </div>
  );
}

/** A headline tile: the value, its change since last month, and the team median. */
function HeadlineTile({
  k,
  entry,
  median,
  sub,
}: {
  readonly k: TrainerPerformanceHeadlineKey;
  readonly entry: TrainerPerformanceEntry;
  readonly median: TrainerPerformanceHeadline;
  readonly sub?: string | undefined;
}): JSX.Element {
  const metric = METRICS[k];
  const teamLine = `team ${metric.format(median[k])}`;
  return (
    <Tile
      label={metric.label}
      value={metric.format(entry.headline[k])}
      change={
        <Change
          metric={metric}
          now={entry.headline[k]}
          before={entry.previous_headline[k]}
        />
      }
      sub={sub === undefined ? teamLine : `${sub} · ${teamLine}`}
    />
  );
}

function MemberLink({
  id,
  names,
}: {
  readonly id: string;
  readonly names: Readonly<Record<string, string>>;
}): JSX.Element {
  return (
    <Link to={`/members/${encodeURIComponent(id)}`}>
      {names[id] ?? `${id.slice(0, 8)}…`}
    </Link>
  );
}

function Section({
  title,
  aside,
  children,
}: {
  readonly title: string;
  readonly aside?: string | undefined;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <>
      <div className="pr-section-head">
        <h2 className="pr-heading">{title}</h2>
        {aside !== undefined ? <span className="subtle">{aside}</span> : null}
      </div>
      {children}
    </>
  );
}

// ---------------------------------------------------------------------------
// The page.
// ---------------------------------------------------------------------------

export function TrainerPerformance(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const thisMonth = useMemo(() => currentStudioMonth(), []);
  const lastComplete = shiftMonth(thisMonth, -1);
  const requested = params.get("month");
  const month =
    requested !== null && MONTH_RE.test(requested) && requested <= thisMonth
      ? requested
      : lastComplete;

  const [data, setData] = useState<TrainerPerformanceResponse | null>(null);
  const [loadedMonth, setLoadedMonth] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    api
      .trainerPerformance(month)
      .then((r) => {
        if (!live) return;
        setData(r);
        setLoadedMonth(month);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [month]);

  const select = (next: { month?: string; trainer?: string }): void => {
    const p = new URLSearchParams(params);
    if (next.month !== undefined) p.set("month", next.month);
    if (next.trainer !== undefined) p.set("trainer", next.trainer);
    setParams(p, { replace: true });
  };

  const months = useMemo(
    () => Array.from({ length: MONTHS_OFFERED }, (_, i) => shiftMonth(thisMonth, -i)),
    [thisMonth],
  );

  if (error !== null) {
    return <p className="empty">Couldn&apos;t load trainer performance ({error}).</p>;
  }
  if (data === null) return <p className="empty">Loading trainer performance…</p>;

  const { report, member_names: names } = data;
  const loading = loadedMonth !== month;
  const reviewable = report.trainers;
  const requestedTrainer = params.get("trainer");
  const selected =
    reviewable.find((t) => t.trainer_id === requestedTrainer) ??
    reviewable.find((t) => !t.admin_linked) ??
    reviewable[0] ??
    null;
  const median = report.team.median;

  return (
    <>
      <div className="pr-section-head tp-head">
        <h1 className="page-title">Trainer performance — {monthLabel(report.month)}</h1>
        <select
          value={month}
          onChange={(e) => select({ month: e.target.value })}
          aria-label="Month"
        >
          {months.map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
              {m === thisMonth ? " (so far)" : ""}
            </option>
          ))}
        </select>
      </div>
      <p className="subtle pr-overview-note">
        {report.is_complete_month
          ? "Complete month"
          : "Month to date — sessions, seats and ratings count once they have ended"}
        {" · "}pay: {paySourceLabel(report.pay)}
        {" · "}team medians over {report.team.trainers_in_median} trainer
        {report.team.trainers_in_median === 1 ? "" : "s"} (owner excluded)
        {" · "}computed {fmtDateTime(report.computed_at)}
        {loading ? " · loading…" : ""}
      </p>

      {reviewable.length === 0 ? (
        <p className="empty">No trainer activity this month.</p>
      ) : (
        <>
          <Section title="The team" aside="change since last month under each figure">
            <div className="table-scroll-x">
              <table className="pr-people-table table-cards tp-team-table">
                <thead>
                  <tr>
                    <th>Trainer</th>
                    {TEAM_COLUMNS.map((k) => (
                      <th key={k} className="pr-num">
                        {METRICS[k].label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reviewable.map((t) => (
                    <tr
                      key={t.trainer_id}
                      className={t.trainer_id === selected?.trainer_id ? "tp-row-selected" : ""}
                    >
                      <td className="table-card-title">
                        <button
                          type="button"
                          className="linklike"
                          onClick={() => select({ trainer: t.trainer_id })}
                        >
                          {t.display_name ?? t.trainer_id.slice(0, 8)}
                        </button>{" "}
                        {t.admin_linked ? (
                          <span className="tag" title="Not in the team medians">
                            owner
                          </span>
                        ) : null}
                      </td>
                      {TEAM_COLUMNS.map((k) => (
                        <td key={k} data-label={METRICS[k].label} className="pr-num">
                          {METRICS[k].format(t.headline[k])}
                          <Change
                            metric={METRICS[k]}
                            now={t.headline[k]}
                            before={t.previous_headline[k]}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="tp-median-row">
                    <td className="table-card-title">Team median</td>
                    {TEAM_COLUMNS.map((k) => (
                      <td key={k} data-label={METRICS[k].label} className="pr-num">
                        {METRICS[k].format(median[k])}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            {report.team.unattributed_repeat_purchases > 0 ? (
              <p className="subtle">
                {report.team.unattributed_repeat_purchases} repeat purchase
                {report.team.unattributed_repeat_purchases === 1 ? "" : "s"} this
                month came from members nobody trained in the{" "}
                {report.definitions.repeat_purchase_lookback_days} days before — not
                credited to anyone.
              </p>
            ) : null}
          </Section>

          {selected !== null ? (
            <TrainerReview
              key={selected.trainer_id}
              month={report.month}
              entry={selected}
              trainers={reviewable}
              median={median}
              names={names}
              pay={report.pay}
              graceHours={report.definitions.consultation_grace_hours}
              onSelect={(id) => select({ trainer: id })}
            />
          ) : null}
        </>
      )}

      <Definitions report={data.report} />
    </>
  );
}

/**
 * "Open PDF" (2026-10-23): the trainer's monthly review PDF, as the review
 * lane renders it. The tab opens inside the click, so a pop-up blocker lets it
 * through, and shows the PDF once its bytes have arrived (a plain link could
 * not carry the sign-in).
 */
function OpenScorecardPdf({
  month,
  trainerId,
}: {
  readonly month: string;
  readonly trainerId: string;
}): JSX.Element {
  const [state, setState] = useState<"idle" | "loading" | "failed">("idle");
  const open = async (): Promise<void> => {
    const tab = window.open("", "_blank");
    setState("loading");
    try {
      const blob = await api.trainerScorecardPdf(month, trainerId);
      const href = URL.createObjectURL(blob);
      if (tab !== null) tab.location.href = href;
      else window.open(href, "_blank");
      // Long enough for the tab to load it; the bytes name people and pay.
      window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
      setState("idle");
    } catch {
      tab?.close();
      setState("failed");
    }
  };
  return (
    <>
      <button
        type="button"
        className="pr-toggle"
        disabled={state === "loading"}
        onClick={() => void open()}
      >
        {state === "loading" ? "Opening…" : "Open PDF"}
      </button>
      {state === "failed" ? <span className="subtle"> Could not open the PDF.</span> : null}
    </>
  );
}

/**
 * The "Monthly review" panel (trainer-review-lane, 2026-10-23): how far this
 * trainer's monthly review got, the four answers they tapped, and the owner's
 * private notes. Admins only — anyone else is answered 403 by the server, and
 * the panel then renders nothing.
 */
function MonthlyReviewPanel({
  month,
  trainerId,
}: {
  readonly month: string;
  readonly trainerId: string;
}): JSX.Element | null {
  const [load, setLoad] = useState<
    | { readonly kind: "loading" }
    | { readonly kind: "hidden" }
    | { readonly kind: "failed" }
    | { readonly kind: "ready"; readonly review: TrainerReviewSummary | null }
  >({ kind: "loading" });
  const [notes, setNotes] = useState("");
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "failed">("idle");

  useEffect(() => {
    let live = true;
    setLoad({ kind: "loading" });
    api
      .trainerReviews(month)
      .then((res) => {
        if (!live) return;
        const review = res.reviews.find((r) => r.trainer_id === trainerId) ?? null;
        setLoad({ kind: "ready", review });
        setNotes(review?.owner_notes ?? "");
        setSave("idle");
      })
      .catch((e: unknown) => {
        if (!live) return;
        setLoad(
          e instanceof ApiError && e.status === 403 ? { kind: "hidden" } : { kind: "failed" },
        );
      });
    return () => {
      live = false;
    };
  }, [month, trainerId]);

  if (load.kind === "hidden") return null;
  if (load.kind === "loading") {
    return (
      <Section title="Monthly review">
        <p className="subtle">Loading…</p>
      </Section>
    );
  }
  if (load.kind === "failed") {
    return (
      <Section title="Monthly review">
        <p className="subtle">Could not load the review.</p>
      </Section>
    );
  }
  const review = load.review;
  const saveNotes = async (): Promise<void> => {
    setSave("saving");
    try {
      await api.saveTrainerReviewNotes({ trainer_id: trainerId, month, notes });
      setSave("saved");
    } catch {
      setSave("failed");
    }
  };
  return (
    <Section
      title="Monthly review"
      aside={
        review !== null && review.questions_sent > 0
          ? `${review.questions_sent} of 4 questions sent`
          : undefined
      }
    >
      <p>{reviewStatusLine(review, fmtDateTime)}</p>
      {reviewCallLine(review, fmtDateTime) !== null ? (
        <p data-testid="tp-review-call">{reviewCallLine(review, fmtDateTime)}</p>
      ) : null}
      <table className="table-cards">
        <tbody>
          {reviewAnswers(review).map((a) => (
            <tr key={a.question}>
              <td data-label="Question">{a.question}</td>
              <td data-label="Answer">{a.answer}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <label className="tp-notes">
        <span className="subtle">Your notes — private, never sent to the trainer</span>
        <textarea
          value={notes}
          rows={4}
          maxLength={5000}
          onChange={(e) => {
            setNotes(e.target.value);
            setSave("idle");
          }}
        />
      </label>
      <div className="tp-notes-actions">
        <button type="button" disabled={save === "saving"} onClick={() => void saveNotes()}>
          {save === "saving" ? "Saving…" : "Save notes"}
        </button>
        {save === "saved" ? <span className="subtle"> Saved.</span> : null}
        {save === "failed" ? <span className="subtle"> Could not save the notes.</span> : null}
      </div>
    </Section>
  );
}

function TrainerReview({
  month,
  entry,
  trainers,
  median,
  names,
  pay,
  graceHours,
  onSelect,
}: {
  readonly month: string;
  readonly entry: TrainerPerformanceEntry;
  readonly trainers: ReadonlyArray<TrainerPerformanceEntry>;
  readonly median: TrainerPerformanceHeadline;
  readonly names: Readonly<Record<string, string>>;
  readonly pay: TrainerPerformancePayMeta;
  readonly graceHours: number;
  readonly onSelect: (trainerId: string) => void;
}): JSX.Element {
  const k = entry.kpis;
  const e = entry.evidence;
  const name = entry.display_name ?? entry.trainer_id.slice(0, 8);

  const { above, below } = glanceAgainstTeam(entry.headline, median);

  return (
    <>
      <div className="pr-section-head">
        <h2 className="pr-heading">Review</h2>
        <span>
          <OpenScorecardPdf month={month} trainerId={entry.trainer_id} />{" "}
          <Link to={`/trainers/${encodeURIComponent(entry.trainer_id)}`}>
            Trainer page →
          </Link>
        </span>
      </div>
      <div className="seg-buttons tp-chooser" role="tablist" aria-label="Trainer">
        {trainers.map((t) => (
          <button
            key={t.trainer_id}
            type="button"
            role="tab"
            aria-selected={t.trainer_id === entry.trainer_id}
            className={`seg-btn${t.trainer_id === entry.trainer_id ? " seg-btn-active" : ""}`}
            onClick={() => onSelect(t.trainer_id)}
          >
            <span className="seg-btn-num">{t.display_name ?? t.trainer_id.slice(0, 8)}</span>
            <span className="seg-btn-label">
              {t.kpis.sessions.delivered} sessions{t.admin_linked ? " · owner" : ""}
            </span>
          </button>
        ))}
      </div>

      <MonthlyReviewPanel month={month} trainerId={entry.trainer_id} />

      <Section title={`${name} at a glance`} aside="against the team median">
        {above.length === 0 && below.length === 0 ? (
          <p className="subtle">In line with the team on every ratio that has numbers.</p>
        ) : (
          <div className="tp-glance">
            {above.length > 0 ? (
              <div>
                <div className="tp-glance-head score-good">Above the team</div>
                <ul>
                  {above.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {below.length > 0 ? (
              <div>
                <div className="tp-glance-head score-poor">To talk about</div>
                <ul>
                  {below.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </Section>

      <Section title="Pay" aside={paySourceLabel(pay)}>
        {k.pay === null ? (
          <p className="empty">This studio has no payroll configuration.</p>
        ) : (
          <>
            <div className="stat-grid">
              <HeadlineTile k="pay_total_minor" entry={entry} median={median} />
              <HeadlineTile
                k="bonus_minor"
                entry={entry}
                median={median}
                sub={`sign-up ${money(k.pay.by_component["bonus_new_signup"] ?? 0)} · renewal ${money(k.pay.by_component["bonus_renewal"] ?? 0)}`}
              />
              <HeadlineTile k="revenue_minor" entry={entry} median={median} />
              <HeadlineTile k="pay_to_revenue_pct" entry={entry} median={median} />
            </div>
            <div className="table-scroll-x">
              <table className="pr-people-table table-cards">
                <tbody>
                  {Object.entries(k.pay.by_component)
                    .sort(([, a], [, b]) => b - a)
                    .map(([component, amount]) => (
                      <tr key={component}>
                        <td className="table-card-title">
                          {COMPONENT_LABELS[component] ?? component}
                        </td>
                        <td data-label="Amount" className="pr-num">
                          {money(amount, true)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <p className="subtle">
              {k.pay.unpriced_sessions > 0
                ? `${k.pay.unpriced_sessions} session${k.pay.unpriced_sessions === 1 ? "" : "s"} could not be priced and were paid the flat rate — see the data gaps on `
                : "The line-by-line statement is on "}
              <Link to="/payroll">Payroll</Link>. Bonus counts here follow payroll&apos;s
              own sign-up/renewal rule, which differs from the sales numbers below.
            </p>
          </>
        )}
      </Section>

      <Section title="Sessions and occupancy">
        <div className="stat-grid">
          <HeadlineTile k="delivered" entry={entry} median={median} />
          <HeadlineTile k="hours" entry={entry} median={median} />
          <HeadlineTile
            k="occupancy_pct"
            entry={entry}
            median={median}
            sub={`${k.occupancy.booked} of ${k.occupancy.capacity} seats, ${k.occupancy.slots} slots`}
          />
          <HeadlineTile k="no_shows" entry={entry} median={median} />
          <HeadlineTile
            k="late_cancels"
            entry={entry}
            median={median}
            sub="credit kept"
          />
        </div>
      </Section>

      <Section title="Clients">
        <div className="stat-grid">
          <HeadlineTile k="active_clients" entry={entry} median={median} />
          <HeadlineTile k="new_clients" entry={entry} median={median} sub="first session with them" />
          <HeadlineTile
            k="retention_pct"
            entry={entry}
            median={median}
            sub={`${k.clients.retained_with_trainer} of last month's ${k.clients.last_month} back with them; ${k.clients.retained_with_anyone} trained with someone`}
          />
        </div>
      </Section>

      <Section title="Member feedback" aside="ratings 1 (poor) to 3 (excellent)">
        <div className="stat-grid">
          <HeadlineTile k="rating_average" entry={entry} median={median} />
          <HeadlineTile k="rating_count" entry={entry} median={median} />
          <Tile label="Poor" value={String(k.ratings.poor)} />
          <Tile label="Excellent" value={String(k.ratings.excellent)} />
        </div>
        {k.ratings.count < 5 ? (
          <p className="subtle">
            Only {k.ratings.count} rating{k.ratings.count === 1 ? "" : "s"} — too few to
            judge on. Rating requests reach very few members today.
          </p>
        ) : null}
        {e.rating_comments.length > 0 ? (
          <div className="table-scroll-x">
            <table className="pr-people-table table-cards">
              <tbody>
                {e.rating_comments.map((r) => (
                  <tr key={r.feedback_id}>
                    <td className="table-card-title">
                      <MemberLink id={r.member_id} names={names} />
                    </td>
                    <td data-label="Session">{fmtDate(r.rated_at)}</td>
                    <td data-label="Rating" className={r.rating === 1 ? "score-poor" : r.rating === 3 ? "score-good" : ""}>
                      {r.rating} / 3
                    </td>
                    <td data-label="Comment">{r.comment ?? <span className="dc-muted">no comment</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Section>

      <Section title="Sales">
        <div className="stat-grid">
          <HeadlineTile
            k="trials"
            entry={entry}
            median={median}
            sub={`${k.sales.trials_signed_up} signed up (${pct(k.sales.trial_conversion_pct)})`}
          />
          <HeadlineTile
            k="trial_conversion_pct_3m"
            entry={entry}
            median={median}
            sub={`${k.sales.trials_signed_up_3m} of ${k.sales.trials_3m} trials`}
          />
          <HeadlineTile k="signups" entry={entry} median={median} />
          <HeadlineTile k="repeat_purchases" entry={entry} median={median} />
        </div>
        <PurchaseTable title="Sign-ups" rows={e.signups} names={names} />
        <PurchaseTable title="Repeat purchases" rows={e.repeat_purchases} names={names} />
        {e.trials_not_signed_up.length > 0 ? (
          <>
            <h3 className="seg-subtitle">Trials not signed up (yet)</h3>
            <div className="table-scroll-x">
              <table className="pr-people-table table-cards">
                <tbody>
                  {e.trials_not_signed_up.map((t) => (
                    <tr key={t.booking_id}>
                      <td className="table-card-title">
                        <MemberLink id={t.member_id} names={names} />
                      </td>
                      <td data-label="Trial">{fmtDate(t.session_ended_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </Section>

      <Section title="SOPs">
        <div className="stat-grid">
          <HeadlineTile
            k="postsession_pct"
            entry={entry}
            median={median}
            sub={`${k.sops.postsession.compliant} of ${k.sops.postsession.graded} prompts answered`}
          />
          <HeadlineTile
            k="consultation_pct"
            entry={entry}
            median={median}
            sub={`${k.sops.consultation.on_file} of ${k.sops.consultation.due} trials${k.sops.consultation.not_yet_due > 0 ? `; ${k.sops.consultation.not_yet_due} still inside ${graceHours} h` : ""}`}
          />
        </div>
        {k.sops.postsession.graded > 0 ? (
          <p className="tp-tiers">
            {(Object.keys(TIER_LABELS) as PromptOutcomeTier[])
              .filter((tier) => k.sops.postsession.by_tier[tier] > 0)
              .map((tier) => (
                <span
                  key={tier}
                  className={`tag ${
                    tier === "captured" || tier === "captured_unkeyed" || tier === "captured_profile"
                      ? "tag-success"
                      : tier === "silent"
                        ? "tag-error"
                        : "tag-warn"
                  }`}
                >
                  {TIER_LABELS[tier]} · {k.sops.postsession.by_tier[tier]}
                </span>
              ))}
          </p>
        ) : null}
        {e.postsession_misses.length > 0 ? (
          <>
            <h3 className="seg-subtitle">Post-session prompts not followed through</h3>
            <div className="table-scroll-x">
              <table className="pr-people-table table-cards">
                <tbody>
                  {e.postsession_misses.map((m) => (
                    <tr key={m.booking_id}>
                      <td className="table-card-title">
                        <MemberLink id={m.member_id} names={names} />
                      </td>
                      <td data-label="Session">{fmtDate(m.session_ended_at)}</td>
                      <td data-label="What happened">{TIER_LABELS[m.tier]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
        {e.consultations_missing.length > 0 ? (
          <>
            <h3 className="seg-subtitle">Trials with no consultation on file</h3>
            <div className="table-scroll-x">
              <table className="pr-people-table table-cards">
                <tbody>
                  {e.consultations_missing.map((c) => (
                    <tr key={c.booking_id}>
                      <td className="table-card-title">
                        <MemberLink id={c.member_id} names={names} />
                      </td>
                      <td data-label="Trial">{fmtDate(c.session_ended_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </Section>
    </>
  );
}

function PurchaseTable({
  title,
  rows,
  names,
}: {
  readonly title: string;
  readonly rows: ReadonlyArray<TrainerPerformancePurchase>;
  readonly names: Readonly<Record<string, string>>;
}): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <>
      <h3 className="seg-subtitle">{title}</h3>
      <div className="table-scroll-x">
        <table className="pr-people-table table-cards">
          <tbody>
            {rows.map((p) => (
              <tr key={p.purchase_id}>
                <td className="table-card-title">
                  <MemberLink id={p.member_id} names={names} />
                </td>
                <td data-label="Bought">{productLabel(p)}</td>
                <td data-label="On">{fmtDate(p.purchased_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Definitions({
  report,
}: {
  readonly report: TrainerPerformanceResponse["report"];
}): JSX.Element {
  const d = report.definitions;
  return (
    <details className="tp-definitions">
      <summary>How these are counted</summary>
      <ul>
        <li>
          <b>The month</b> is the studio&apos;s calendar month; a session belongs to the
          month its slot ends in, like payroll. Before the month ends, everything is
          month to date.
        </li>
        <li>
          <b>Sessions</b> are completed bookings that were not a no-show; hours count
          each slot once. <b>Late cancels</b> are cancellations where the member&apos;s
          credit was kept.
        </li>
        <li>
          <b>Occupancy</b> is seats booked ÷ seats offered on the trainer&apos;s slots. A
          hidden slot was never offered to members, so it counts only the seats staff
          booked into it.
        </li>
        <li>
          <b>Clients</b> are members with a session with this trainer; <b>new</b> means
          their first session with them ever; <b>retention</b> is the share of last
          month&apos;s clients who trained with them again.
        </li>
        <li>
          <b>A trial</b> is a newcomer&apos;s first session, on the introduction offer.{" "}
          <b>A sign-up</b> is their first multi-session pack or membership after it,
          credited to the trial&apos;s trainer. <b>A repeat purchase</b> is every other
          multi-session pack or membership, credited to whoever trained the member most
          in the {d.repeat_purchase_lookback_days} days before. These are not
          payroll&apos;s bonus categories, which cannot tell a long-standing client&apos;s
          first purchase in Clavis from a new one.
        </li>
        <li>
          <b>Post-session replies</b>: of the AI&apos;s post-session prompts to the
          trainer, the share answered with something saved to the member (a session
          note, or the profile) within {d.reply_window_hours} hours — the same grading
          as the morning digest.
        </li>
        <li>
          <b>Consultation on file</b>: of this month&apos;s trials more than{" "}
          {d.consultation_grace_hours} hours old, the share whose member has a goal or
          focus area on file (trials before {d.compliance_epoch} are not counted).
        </li>
        <li>
          <b>Pay</b> is the stored payroll run when there is one (adjustments included),
          otherwise a provisional dry-run; <b>revenue</b> is the value of the sessions
          they delivered. <b>Team medians</b> leave out the owner and anyone with no
          sessions that month.
        </li>
      </ul>
    </details>
  );
}
